import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Rocket, Building } from "lucide-react";

interface ApplicationCardsProps {
  onTechstarsClick: () => void;
  onOtherClick: () => void;
}

const ApplicationCards = ({ onTechstarsClick, onOtherClick }: ApplicationCardsProps) => {
  return (
    <section className="container mx-auto px-6 py-16">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Choose Your Application Path</h2>
        <p className="text-xl text-gray-600">Fast-track approval for recognized accelerator programs</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Techstars Card */}
        <Card className="relative overflow-hidden border-2 border-blue-200 hover:border-blue-300 transition-colors">
          <div className="absolute top-4 right-4">
            <Badge className="bg-blue-100 text-blue-800">Fast Track</Badge>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Rocket className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Techstars Members</CardTitle>
                <CardDescription>Automatic approval in 24 hours</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Instant verification with batch info</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Access to Techstars-specific features</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Priority customer support</span>
              </div>
            </div>
            <Button 
              className="w-full" 
              onClick={onTechstarsClick}
            >
              Apply as Techstars Member
            </Button>
          </CardContent>
        </Card>

        {/* Other Accelerators Card */}
        <Card className="relative overflow-hidden border-2 border-purple-200 hover:border-purple-300 transition-colors">
          <div className="absolute top-4 right-4">
            <Badge variant="secondary">2-3 Days</Badge>
          </div>
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <Building className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-xl">Other Accelerators</CardTitle>
                <CardDescription>Manual review for all programs</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Y Combinator, 500 Global, etc.</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Boutique and regional accelerators</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Same full feature access</span>
              </div>
            </div>
            <Button 
              className="w-full" 
              variant="outline"
              onClick={onOtherClick}
            >
              Apply from Other Accelerator
            </Button>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ApplicationCards;
